import { Check } from "lucide-react";
import {
  PageHero,
  Eyebrow,
  ArrowLink,
  Reveal,
  FinalCTA,
  usePageMeta,
} from "../components/Common";

const QUALIFICATIONS = [
  "Master of Education in Educational Leadership",
  "Graduate Certificate in Educational Leadership",
  "International teaching and educational leadership experience across Australia, Japan and Germany",
];

const ENGAGEMENTS = [
  "Independent consulting projects",
  "Short-term strategic advisory assignments",
  "International recruitment and partnerships",
  "Education technology and LMS consultation",
  "Educational change and implementation support",
  "Leadership and staff development",
  "Cross-cultural communication workshops",
];

const About = () => {
  usePageMeta(
    "About David Duenzl — English for Leaders",
    "Founder-led international education advisory. Master of Education in Educational Leadership; leadership experience across Australia, Japan and Germany.",
    "/about"
  );

  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About"
        title="David Duenzl"
        intro="Founder of English for Leaders — an independent, founder-led advisory practice for international education."
      />

      {/* Biography */}
      <section className="bg-white" data-testid="about-bio">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-6 py-16 lg:grid-cols-[5fr_7fr] lg:gap-20 lg:px-10 lg:py-24">
          <Reveal className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div
              className="absolute -left-3 -top-3 h-full w-full border-2 border-crimson"
              aria-hidden="true"
            />
            <img
              src="/assets/david-headshot.png"
              alt="David Duenzl, founder of English for Leaders"
              className="relative w-full object-cover"
              loading="lazy"
              data-testid="about-photo"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>The Founder</Eyebrow>
            <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl">
              International Experience. Practical Insight.
            </h2>
            <p className="mt-7 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              My career has taken me across Australia, Japan and Germany, working
              in educational leadership, international partnerships, recruitment,
              teaching and organisational change.
            </p>
            <p className="mt-5 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              With a Master of Education in Educational Leadership and practical
              experience across international curricula, school management and
              educational technology, I bring an informed, collaborative approach
              to every engagement.
            </p>
            <p className="mt-5 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              English for Leaders exists for a simple reason: in international
              education, language and leadership are inseparable. The leaders who
              communicate clearly — across cultures, across languages, across
              difficult conversations — are the ones whose schools move forward.
            </p>
            <p className="mt-5 text-base leading-relaxed text-charcoal/80 sm:text-lg">
              Alongside international schools, English for Leaders has supported
              professional communication and English language development for
              organisations including Uber HQ, Adidas, Kaufland and HESTA Super,
              and collaborated with Swinburne University of Technology and RMIT
              University on English immersion study tours.
            </p>
            <div className="mt-8">
              <ArrowLink to="/experience" testid="about-experience-link">
                Explore My Experience
              </ArrowLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Executive portrait band */}
      <section className="bg-charcoal" data-testid="about-portrait-band">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[320px] lg:min-h-[520px]">
            <img
              src="/assets/david-executive-bw.png"
              alt="David Duenzl — executive portrait"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              data-testid="about-executive-photo"
            />
          </div>
          <div className="flex items-center px-6 py-16 lg:px-16 lg:py-24">
            <Reveal>
              <Eyebrow light>The Practice</Eyebrow>
              <h2 className="mt-5 font-display text-3xl leading-tight tracking-tight text-white sm:text-4xl">
                Bridging Cultures in International Education.
              </h2>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
                English for Leaders is intentionally a founder-led practice. When
                we work together, you work directly with me — from the first
                conversation to the final recommendation. No handoffs, no account
                managers, no diluted advice.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" testid="about-contact-link">
                  Let's Connect
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Qualifications + engagement */}
      <section className="bg-offwhite" data-testid="about-qualifications">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-24">
          <Reveal>
            <Eyebrow>Qualifications</Eyebrow>
            <h2 className="mt-5 font-display text-2xl tracking-tight text-navy sm:text-3xl">
              Credentials that ground the work.
            </h2>
            <ul className="mt-7 space-y-4">
              {QUALIFICATIONS.map((q) => (
                <li
                  key={q}
                  className="flex items-start gap-4 border-b border-navy/10 pb-4 text-base leading-relaxed text-charcoal/85"
                >
                  <Check className="mt-1 h-5 w-5 shrink-0 text-crimson" strokeWidth={2.5} />
                  {q}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Engagements</Eyebrow>
            <h2 className="mt-5 font-display text-2xl tracking-tight text-navy sm:text-3xl">
              How we can work together.
            </h2>
            <ul className="mt-7 space-y-4">
              {ENGAGEMENTS.map((e) => (
                <li
                  key={e}
                  className="flex items-start gap-4 border-b border-navy/10 pb-4 text-base leading-relaxed text-charcoal/85"
                >
                  <Check className="mt-1 h-5 w-5 shrink-0 text-crimson" strokeWidth={2.5} />
                  {e}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default About;
